import { getAccessToken, getRefreshToken } from "./utils";

export const cleanDataLayerPayload = (payload) => {
  if (payload === null || payload === undefined || payload === "" || payload === "NA" || payload === "na") {
    return undefined;
  }

  if (payload instanceof Date) {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload
      .map(item => cleanDataLayerPayload(item))
      .filter(item => {
        if (item === undefined) return false;
        if (typeof item === "object" && !(item instanceof Date) && Object.keys(item).length === 0) {
          return false;
        }
        return true;
      });
  }

  if (typeof payload === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(payload)) {
      if (
        value === "NA" ||
        value === "na" ||
        value === null ||
        value === undefined ||
        value === "" ||
        (key === "premium_article" && (value === "no" || value === false))
      ) {
        continue;
      }
      if (typeof value === "object") {
        const cleanedVal = cleanDataLayerPayload(value);
        if (cleanedVal === undefined) {
          continue;
        }
        cleaned[key] = cleanedVal;
      } else {
        cleaned[key] = value;
      }
    }
    return cleaned;
  }

  return payload;
};

export const getFlowId = () => {
  if (typeof window === "undefined") return "NA";

  const { uuid } = getAuthStatus();
  if (uuid && uuid !== "NA") {
    const activePlanName = sessionStorage.getItem(`active_plan_name_${uuid}`);
    if (activePlanName && activePlanName !== "NA") {
      return "NA";
    }
  }

  let flowId = sessionStorage.getItem("user_flow_id");
  if (!flowId) {
    flowId = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2, 15) + "-" + Date.now();
    sessionStorage.setItem("user_flow_id", flowId);
  }

  return flowId;
};

export const getArticleContext = () => {
  let articleContext = { article_id: "NA", section_name: "NA", premium_article: "NA" };
  if (typeof window === "undefined") return articleContext;
  try {
    const savedContext = sessionStorage.getItem("last_article_context");
    if (savedContext) {
      const parsed = JSON.parse(savedContext);
      if (parsed.article_id) articleContext.article_id = parsed.article_id;
      if (parsed.section_name) articleContext.section_name = parsed.section_name;
      if (parsed.premium_article !== undefined) articleContext.premium_article = parsed.premium_article;
    }
  } catch (err) {}
  return articleContext;
};

export const fullyDecodeURI = (uri) => {
  let decoded = uri;
  try {
    while (decoded !== decodeURIComponent(decoded)) {
      decoded = decodeURIComponent(decoded);
    }
  } catch (e) {}
  return decoded;
};

export const getGaClientId = () => {
  if (typeof window === "undefined") return "NA";
  const match = document.cookie.match(/(?:^|;)\s*_ga=([^;]*)/);
  if (match && match[1]) {
    const parts = match[1].split(".");
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  return "NA";
};

export const getGaSessionId = () => {
  if (typeof window === "undefined") return "NA";

  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // A GA4 session cookie starts with _ga_ followed by the container ID
    if (cookie.startsWith("_ga_")) {
      const cookieValue = cookie.split("=")[1];
      if (cookieValue) {
        const decodedValue = decodeURIComponent(cookieValue);

        // Handle newer GS2.1 format
        if (decodedValue.startsWith("GS2.1.")) {
          const parts = decodedValue.split(".");
          if (parts.length >= 3) {
            const payload = parts[2];
            if (payload) {
              const sessionPart = payload.split("$").find(p => p.startsWith("s"));
              if (sessionPart) {
                return sessionPart.substring(1);
              }
            }
          }
        }

        // Handle older GS1.1 format
        if (decodedValue.startsWith("GS1.1.")) {
          const parts = decodedValue.split(".");
          if (parts.length >= 3) {
            return parts[2];
          }
        }
      }
    }
  }

  return "NA";
};

export const pushSubscribeClickEvent = (source = "NA") => {
  if (typeof window === "undefined") return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

  const articleContext = getArticleContext();

  const eventPayload = {
    event: "subscribe_click",
    auth_status: formattedStatus,
    page_path: fullyDecodeURI(window.location.pathname + window.location.search),
    subscribe_source: source
  };

  if (status === "logged_in" && uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const pushViewPlansEvent = (plans = [], source = "NA") => {
  if (typeof window === "undefined" || !plans || plans.length === 0) return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

  const articleContext = getArticleContext();

  const items = plans.map((plan, index) => {
    let category = "NA";

    if (plan.duration_unit) {
      const unit = (plan.duration_unit || "").toLowerCase();
      const length = plan.duration_length || 1;

      if (unit.includes("year")) category = "yearly";
      else if (unit.includes("month")) {
        if (length === 12) category = "yearly";
        else category = length === 1 ? "monthly" : `${length}-months`;
      } else if (unit.includes("day")) {
        category = length === 1 ? "daily" : `${length}-days`;
      }
    }

    if (category === "NA" && (plan.plan_name || plan.title)) {
      const name = (plan.plan_name || plan.title || "").toLowerCase();
      if (name.includes("year")) category = "yearly";
      else if (name.includes("month")) category = "monthly";
      else if (name.includes("day")) category = "daily";
    }

    const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;

    return {
      item_id: String(plan.id || ""),
      item_name: plan.title || plan.plan_name || "NA",
      item_category: category,
      price: priceCents / 100,
      currency: plan.price_currency || "INR",
      index: index + 1
    };
  });

  const eventPayload = {
    event: "view_plans",
    auth_status: formattedStatus,
    item_list_name: "subscribe_page",
    items: items,
    subscribe_source: source
  };

  if (status === "logged_in" && uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const resolveCampaign = (campaign) => {
  if (campaign && campaign !== "NA" && campaign !== "na") return campaign;
  if (typeof window !== "undefined") {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const c = searchParams.get("utm_campaign") || searchParams.get("campaign");
      if (c && c !== "NA" && c !== "na") return c;
    } catch (e) {}
  }
  return undefined;
};

export const pushBeginCheckoutEvent = (plan = null, source = "NA", campaign = null, loginInFlow = false, index = 1) => {
  if (typeof window === "undefined" || !plan) return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;
  const campaignVal = resolveCampaign(campaign);

  let articleContext = { article_id: "NA", section_name: "NA", premium_article: "NA" };
  try {
    const savedContext = sessionStorage.getItem("last_article_context");
    if (savedContext) {
      const parsed = JSON.parse(savedContext);
      if (parsed.article_id) articleContext.article_id = parsed.article_id;
      if (parsed.section_name) articleContext.section_name = parsed.section_name;
      if (parsed.premium_article !== undefined) articleContext.premium_article = parsed.premium_article;
    }
  } catch (err) {}

  let category = "NA";
  if (plan.duration_unit) {
    const unit = (plan.duration_unit || "").toLowerCase();
    const length = plan.duration_length || 1;
    if (unit.includes("year")) category = "yearly";
    else if (unit.includes("month")) {
      if (length === 12) category = "yearly";
      else category = length === 1 ? "monthly" : `${length}-months`;
    } else if (unit.includes("day")) {
      category = length === 1 ? "daily" : `${length}-days`;
    }
  }

  if (category === "NA" && (plan.plan_name || plan.title)) {
    const name = (plan.plan_name || plan.title || "").toLowerCase();
    if (name.includes("year")) category = "yearly";
    else if (name.includes("month")) category = "monthly";
    else if (name.includes("day")) category = "daily";
  }

  const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;
  const price = priceCents / 100;
  const planName = plan.title || plan.plan_name || "NA";

  const items = [{
    item_id: String(plan.id || ""),
    item_name: planName,
    item_category: category,
    price: price,
    currency: plan.price_currency || "INR",
    index: index
  }];

  const eventPayload = {
    event: "begin_checkout",
    auth_status: formattedStatus,
    plan_name: planName,
    plan_id: String(plan.id || ""),
    plan_price: price,
    currency: plan.price_currency || "INR",
    items: items,
    subscribe_source: source,
    login_in_flow: loginInFlow
  };

  if (status === "logged_in" && uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }
  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const pushSelectPlanEvent = (plan, source = "NA", index = 1, campaign = null) => {
  if (typeof window === "undefined" || !plan) return;

  const { status, uuid } = getAuthStatus();

  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;
  const campaignVal = resolveCampaign(campaign);

  const articleContext = getArticleContext();

  let category = "NA";
  if (plan.duration_unit) {
    const unit = (plan.duration_unit || "").toLowerCase();
    const length = plan.duration_length || 1;
    if (unit.includes("year")) category = "yearly";
    else if (unit.includes("month")) {
      if (length === 12) category = "yearly";
      else category = length === 1 ? "monthly" : `${length}-months`;
    } else if (unit.includes("day")) {
      category = length === 1 ? "daily" : `${length}-days`;
    }
  }
  if (category === "NA" && (plan.plan_name || plan.title)) {
    const name = (plan.plan_name || plan.title || "").toLowerCase();
    if (name.includes("year")) category = "yearly";
    else if (name.includes("month")) category = "monthly";
    else if (name.includes("day")) category = "daily";
  }

  const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;
  const price = priceCents / 100;

  const item = {
    item_id: String(plan.id || ""),
    item_name: plan.title || plan.plan_name || "NA",
    item_category: category,
    price: price,
    currency: plan.price_currency || "INR",
    index: index
  };

  const effectiveLoginInFlow = (formattedStatus !== "logged_in" || Boolean(
    typeof sessionStorage !== "undefined" && (
      sessionStorage.getItem("login_in_this_checkout_flow") ||
      sessionStorage.getItem("sso_login_initiated_source")
    )
  ));

  const eventPayload = {
    event: "select_plan",
    auth_status: formattedStatus,
    value: price,
    currency: plan.price_currency || "INR",
    plan_name: plan.title || plan.plan_name || "NA",
    plan_id: String(plan.id || ""),
    plan_price: price,
    items: [item],
    subscribe_source: source,
    login_in_flow: effectiveLoginInFlow
  };

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  if (status === "logged_in" && uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }
  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const pushLoginInitiatedEvent = (source = "NA", campaign = null) => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem("login_initiated_active", "true");
  } catch (e) {}

  const articleContext = getArticleContext();
  const campaignVal = resolveCampaign(campaign);

  const eventPayload = {
    event: "login_initiated",
    auth_status: "anonymous",
    subscribe_source: source,
    page_path: fullyDecodeURI(window.location.pathname + window.location.search)
  };

  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const pushLoginCompletedEvent = (source = "NA", campaign = null, passedUuid = null, userObj = null) => {
  if (typeof window === "undefined") return;

  // Guard: login_completed should ONLY trigger if login_initiated was fired in this flow (matches tpml)
  let wasInitiated = false;
  try {
    wasInitiated = Boolean(
      sessionStorage.getItem("login_initiated_active") === "true" ||
      sessionStorage.getItem("checkout_login_initiated") === "true" ||
      sessionStorage.getItem("login_in_this_checkout_flow") === "true" ||
      sessionStorage.getItem("sso_login_initiated_source")
    );
  } catch (e) {}

  if (!wasInitiated) {
    console.warn("[DataLayer] login_completed blocked: No preceding login_initiated event.");
    return;
  }

  let { status, uuid } = getAuthStatus();
  const candidateUuid = passedUuid || userObj?.userData?.id || userObj?.id || userObj?.user?.id;
  if (candidateUuid && candidateUuid !== "NA") {
    status = "logged_in";
    uuid = String(candidateUuid).substring(0, 36);
  } else if (status !== "logged_in") {
    try {
      const userStr = typeof window !== "undefined" && window.localStorage?.getItem("user");
      if (userStr) {
        const parsed = JSON.parse(userStr);
        const resolvedId = parsed?.userData?.id || parsed?.id || parsed?.user?.id;
        if (resolvedId && resolvedId !== "NA") {
          status = "logged_in";
          uuid = String(resolvedId).substring(0, 36);
        }
      }
    } catch (e) {}
  }

  if (status !== "logged_in") return;

  // Consume flag so it cannot fire multiple times
  try {
    sessionStorage.removeItem("login_initiated_active");
  } catch (e) {}

  const articleContext = getArticleContext();
  let resolvedSource = source;
  if (!resolvedSource || resolvedSource === "NA") {
    try {
      if (typeof sessionStorage !== "undefined") {
        resolvedSource = sessionStorage.getItem("subscribe_source") || sessionStorage.getItem("sso_login_initiated_source") || "direct";
      }
    } catch (e) {}
  }

  let resolvedCampaign = campaign;
  if (!resolvedCampaign) {
    try {
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        resolvedCampaign = params.get("utm_campaign") || undefined;
      }
    } catch (e) {}
  }
  const campaignVal = resolveCampaign(resolvedCampaign);

  let loginMethod = "email";
  try {
    let data = userObj;
    if (!data && typeof window !== "undefined") {
      const userStr = window.localStorage?.getItem("user");
      if (userStr) {
        data = JSON.parse(userStr);
      }
    }
    if (data) {
      const metadataLoginMethod = data?.metadata?.login_method;
      if (metadataLoginMethod) {
        if (metadataLoginMethod === "email_login") loginMethod = "email";
        else if (metadataLoginMethod === "mobile_login" || metadataLoginMethod === "phone_login") loginMethod = "mobile no";
        else if (metadataLoginMethod === "google_onetap") loginMethod = "google_onetap";
        else loginMethod = metadataLoginMethod;
      } else if (data?.userData?.providers?.includes("google")) {
        loginMethod = "google_icon";
      } else if (data?.userData?.phoneNumbers?.length > 0 || data?.userData?.phone) {
        loginMethod = "mobile no";
      }
    }
  } catch (e) {
    console.error("Error parsing user data", e);
  }

  const eventPayload = {
    event: "login_completed",
    auth_status: "logged_in",
    login_method: loginMethod,
    subscribe_source: resolvedSource || "direct",
    page_path: fullyDecodeURI(window.location.pathname + window.location.search)
  };

  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  if (uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }

  const flowId = getFlowId();
  if (flowId !== "NA") eventPayload.flow_id = flowId;
  if (articleContext.article_id && articleContext.article_id !== "NA") eventPayload.article_id = articleContext.article_id;
  if (articleContext.section_name && articleContext.section_name !== "NA") eventPayload.section_name = articleContext.section_name;
  if (articleContext.premium_article && articleContext.premium_article !== "NA") eventPayload.premium_article = articleContext.premium_article;

  console.log("DataLayer login_completed Event:", eventPayload);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

function removeTokens(url) {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('access_token');
    urlObj.searchParams.delete('refresh_token');
    let cleanUrl = urlObj.toString();
    // Regex fallback for malformed query strings (like ?sds%3Faccess_token)
    cleanUrl = cleanUrl.replace(/(%3F|\?|&)(access_token|refresh_token)=[^&]*/g, '');
    return cleanUrl.replace(/[?&]$/, '');
  } catch (e) {
    return url.replace(/(%3F|\?|&)(access_token|refresh_token)=[^&]*/g, '').replace(/[?&]$/, '');
  }
}

function extractSectionNames(url) {
  const pathname = new URL(url).pathname;

  const sections = pathname
    .replace(/^\/|\/$/g, "")
    .split("/");

  return sections;
}

// Helper function to extract and format section hierarchy from section URL
function getSectionHierarchy(sectionUrl) {
  if (!sectionUrl) return "NA";

  try {
    const sectionParts = extractSectionNames(sectionUrl);
    // Capitalize first letter of each section and join with comma
    return sectionParts
      .map(section => section.charAt(0).toUpperCase() + section.slice(1))
      .join(",");
  } catch (e) {
    return "NA";
  }
}

export { convertTimestampToYYYYMMDDHHMM, convertTimestampToZTime } from "./date-utils";

export const pushEventToDataLayer = (eventName) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({ event: eventName });
  }
};

export const pushPageViewEvent = (eventName, url, title) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      page: {
        url: url,
        title: title
      }
    });
  }
}

let lastSignUpLoginTrackTime = 0;
let lastSignUpLoginTrackUser = null;

const buildLoginPayload = (url, data, isAutoLogin = false) => {
  const userId = data?.userData?.id || data?.id || "NA";

  const loginSourceFromSession = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("sso_login_initiated_source") : null;
  const loginSource = loginSourceFromSession ? loginSourceFromSession : (url ? removeTokens(url) : "NA");

  const marketingConsentStatus = data?.metadata?.receiveMarketingAndPromitionalEmail || false;
  const newsletterConsentStatus = data?.metadata?.dh?.dhNewsLetterTopNewsOfTheDay || false;

  const joinedTime = data?.userData?.timeJoined ? new Date(data.userData.timeJoined) : new Date();
  const accountCreationDate = joinedTime.toISOString();

  const isSignup = Date.now() - joinedTime.getTime() < 5 * 60 * 1000;
  const eventName = isSignup ? "sign_up" : "login";

  let loginMethod = "email";
  const metadataLoginMethod = data?.metadata?.login_method;

  if (metadataLoginMethod) {
    if (metadataLoginMethod === "email_login") loginMethod = "email";
    else if (metadataLoginMethod === "mobile_login" || metadataLoginMethod === "phone_login") loginMethod = "mobile no";
    else if (metadataLoginMethod === "google_onetap") loginMethod = "google_onetap";
    else loginMethod = metadataLoginMethod;
  } else if (isAutoLogin && !loginSourceFromSession) {
    loginMethod = "sso_auto_login";
  } else if (data?.userData?.providers?.includes("google")) {
    loginMethod = "google_icon";
  } else if (data?.userData?.phoneNumbers?.length > 0 || data?.userData?.phone) {
    loginMethod = "mobile no";
  }

  const payload = {
    event: eventName,
    method: loginMethod,
    source: loginSource || "NA",
    marketing_and_promotion_consent: marketingConsentStatus,
    newsletter_consent: newsletterConsentStatus,
    account_created_date: accountCreationDate
  };

  if (userId && userId !== "NA") {
    payload.uuid = userId;
  }

  return cleanDataLayerPayload(payload);
};

export const addSignUpLoginToDataLayer = (url, data) => {
  if (typeof window !== "undefined" && data) {
    const userId = data?.userData?.id || "NA";
    const now = Date.now();
    if (userId !== "NA" && userId === lastSignUpLoginTrackUser && now - lastSignUpLoginTrackTime < 1000) {
      return;
    }
    lastSignUpLoginTrackTime = now;
    lastSignUpLoginTrackUser = userId;

    if (userId !== "NA") {
      localStorage.setItem("last_logged_in_uuid", userId);
    }

    const eventPayload = buildLoginPayload(url, data, false);

    console.log(`DataLayer ${eventPayload.event} Event (Explicit):`, eventPayload);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventPayload);

    if (typeof sessionStorage !== "undefined") {
      if (!sessionStorage.getItem("checkout_login_initiated") && !sessionStorage.getItem("login_in_this_checkout_flow")) {
        sessionStorage.removeItem("sso_login_initiated_source");
      }
    }
  }
};

export const addLogoutToDataLayer = (url, data, source = "NA") => {
  if (typeof window !== "undefined" && data) {
    const userId = data.userData?.id || "NA";
    let subscriptionStatus = "free";
    if (userId !== "NA") {
      const activePlanName = window.sessionStorage.getItem(`active_plan_name_${userId}`);
      if (activePlanName && activePlanName !== "NA") subscriptionStatus = "active";
    }

    window.dataLayer.push({
      event: "logout",
      user_id: userId,
      auth_status: "anonymous",
      source: source,
      subscription_status: subscriptionStatus
    });
  }
}

export const addAuthTrackingToDataLayer = (data) => {
  if (typeof window !== "undefined" && data) {
    const userUuid = data?.userData?.id;
    const lastLoggedInUuid = localStorage.getItem("last_logged_in_uuid");

    // Only fire this once per user session (for returning users)
    if (userUuid && userUuid !== "NA" && userUuid !== lastLoggedInUuid) {
      localStorage.setItem("last_logged_in_uuid", userUuid);

      const eventPayload = buildLoginPayload(window.location.href, data, true);

      console.log(`DataLayer ${eventPayload.event} Event (Auto):`, eventPayload);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(eventPayload);

      if (typeof sessionStorage !== "undefined") {
        if (!sessionStorage.getItem("checkout_login_initiated") && !sessionStorage.getItem("login_in_this_checkout_flow")) {
          sessionStorage.removeItem("sso_login_initiated_source");
        }
      }
    }
  }
}

export const addClickTrackingToDataLayer = (url, text, id) => {
  if (typeof window !== "undefined") {
    window.dataLayer.push({
      event: "menuClick",
      click_id: id,
      click_text: text,
      click_url: url,
    });
  }
}

export const addStoryPageViewToDataLayer = (story, commentCount) => {
  if (typeof window !== "undefined" && story) {
    const storyTags = story.tags?.map(tag => tag.name) || [];

    const contentInfo = {
      content_id: story["story-content-id"] || "",
      content_type: "story",
      content_template: story["story-template"] || "",
      public_identifier: story["public-identifier"] || "",
      language: story?.metadata?.language || "english",
    };

    const authorNames = story?.authors?.map(author => author.name) || ["unknown"];

    const storyDetails = {
      page_type: "story",
      story_template: story["story-template"],
      section_name: extractSectionNames(story.sections?.[0]?.["section-url"]).map(
        section => section.charAt(0).toUpperCase() + section.slice(1)
      ) || "unknown",
      author_name: authorNames,
      creator_name: story["owner-name"] || "unknown",
    };

    window.dataLayer.push({
      event: "storyPageView",
      content_info: contentInfo,
      story_details: storyDetails,
      number_comments: commentCount,
      story_tags: storyTags,
      story_words: story["word-count"] || 0,
      published_date: convertTimestampToZTime(story["first-published-at"]) || "",
      last_updated: convertTimestampToZTime(story["last-published-at"]) || "",
    });
  }
};

// Helper function to get comment count from Coral widget
function getCommentCount() {
  if (typeof window !== "undefined") {
    const commentCountElement = document.querySelector(".coral-count");
    if (commentCountElement) {
      const text = commentCountElement.innerText.trim();
      const count = Number(text);
      // Return 0 if count is 0, otherwise return count or "NA" if not a number
      return isNaN(count) ? "NA" : count;
    }
  }
  return "NA";
}

// Unified function to push data layer for all page types
export const pushPageDataLayer = (pageType, userData, pageData = null) => {
  if (typeof window !== "undefined") {
    let isLoggedIn = false;
    let userId = null;

    let effectiveUser = userData;
    if (!effectiveUser || (!effectiveUser?.userData?.id && !effectiveUser?.id && !effectiveUser?.user?.id)) {
      try {
        const userFromLS = localStorage.getItem('user');
        if (userFromLS) {
          effectiveUser = JSON.parse(userFromLS);
        }
      } catch (e) {
        console.error('[Data Layer] Error reading user from localStorage:', e);
      }
    }

    const authStatusObj = getAuthStatus();
    const candidateId = effectiveUser?.userData?.id || effectiveUser?.id || effectiveUser?.user?.id || (authStatusObj.status === "logged_in" && authStatusObj.uuid !== "NA" ? authStatusObj.uuid : null);

    if (candidateId && candidateId !== "NA") {
      isLoggedIn = true;
      userId = String(candidateId).substring(0, 36);
    } else if (authStatusObj.status === "logged_in") {
      isLoggedIn = true;
      if (authStatusObj.uuid && authStatusObj.uuid !== "NA") {
        userId = authStatusObj.uuid;
      }
    }

    let sanitizedUuid = "NA";
    if (userId) {
      const userIdString = String(userId);
      sanitizedUuid = userIdString.length > 36
        ? userIdString.substring(0, 36)
        : userIdString;
    }

    let dataLayerObject = {
      event: "page_view",
      page_type: "NA",
      author_name: "NA",
      author_id: "NA",
      section_name: "NA",
      published_date: "NA",
      created_date: "NA",
      last_updated_date: "NA",
      content_id: "NA",
      article_id: "NA",
      article_type: "NA",
      story_tags: "NA",
      story_words: "NA",
      creator_name: "NA",
      comment_number: "NA",
      content_title: "NA",
      auth_status: isLoggedIn ? "logged_in" : "anonymous",
      access_level_value: "NA"
    };

    if (isLoggedIn && sanitizedUuid && sanitizedUuid !== "NA") {
      dataLayerObject.uuid = sanitizedUuid;
    }

    if (pageType === "home-page") {
      dataLayerObject.page_type = "home_page";
    } else if (pageType === "subscribe-page") {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const q = pageData?.view || pageData?.q || searchParams.get("q");
        if (q === "review") dataLayerObject.page_type = "proceed_to_pay_page";
        else if (q === "success") dataLayerObject.page_type = "success_page";
        else if (q === "failure") dataLayerObject.page_type = "failure_page";
        else if (q === "subscribe" || !q) dataLayerObject.page_type = "subscribe_plan_page";
        else dataLayerObject.page_type = "subscribe_plan_page";
      } catch (e) {
        dataLayerObject.page_type = "subscribe_plan_page";
      }
    } else if (pageType === "static-page") {
      dataLayerObject.page_type = "static_page";
    } else if (pageType === "story-page" && pageData?.story) {
      const story = pageData.story;

      // Extract author names and IDs
      const authorNames = story.authors?.map(author => author.name).join(",") || "NA";
      const authorIds = story.authors?.map(author => author.id).join(",") || "NA";

      // Extract section name with parent hierarchy (e.g., "India,Andhra Pradesh")
      const sectionName = story.sections?.[0]?.["section-url"]
        ? getSectionHierarchy(story.sections[0]["section-url"])
        : "NA";

      // Extract and format dates
      const publishedDate = convertTimestampToYYYYMMDDHHMM(story["first-published-at"]);
      const lastUpdatedDate = convertTimestampToYYYYMMDDHHMM(story["last-published-at"]);
      const createdDate = convertTimestampToYYYYMMDDHHMM(story["content-created-at"]);

      // Extract tags
      const storyTags = story.tags?.map(tag => tag.name).join(",") || "NA";

      // Update object with story data in consistent order
      dataLayerObject.page_type = "article_page";
      dataLayerObject.author_name = authorNames;
      dataLayerObject.author_id = authorIds;
      dataLayerObject.section_name = sectionName;
      dataLayerObject.published_date = publishedDate;
      dataLayerObject.created_date = createdDate;
      dataLayerObject.last_updated_date = lastUpdatedDate;
      dataLayerObject.content_id = story["story-content-id"] || "NA";
      dataLayerObject.article_id = story["public-identifier"] || "NA";
      dataLayerObject.article_type = story["story-template"] || "NA";
      dataLayerObject.story_tags = storyTags;
      dataLayerObject.story_words = story["word-count"] || "NA";
      dataLayerObject.creator_name = story["owner-name"] || "NA";
      dataLayerObject.comment_number = "NA"; // Will be updated after delay
      dataLayerObject.content_title = story.headline || "NA";

      const isPremium = story?.access === "subscription" || story?.["access-level-value"] === "subscription";

      if (isPremium) {
        dataLayerObject.premium_article = "yes";
      }

      if (story["access-level-value"] !== null && story["access-level-value"] !== undefined) {
        dataLayerObject.access_level_value = String(story["access-level-value"]);
      }

      try {
        const articleContext = {
          author_name: dataLayerObject.author_name,
          content_id: dataLayerObject.content_id,
          article_id: dataLayerObject.article_id,
          article_type: dataLayerObject.article_type,
          author_id: dataLayerObject.author_id,
          content_title: dataLayerObject.content_title,
          creator_name: dataLayerObject.creator_name,
          created_date: dataLayerObject.created_date,
          story_tags: dataLayerObject.story_tags,
          access_level_value: dataLayerObject.access_level_value,
          story_words: dataLayerObject.story_words,
          published_date: dataLayerObject.published_date,
          last_updated_date: dataLayerObject.last_updated_date,
          section_name: dataLayerObject.section_name,
          comment_number: dataLayerObject.comment_number
        };
        if (isPremium) {
          articleContext.premium_article = "yes";
        }
        sessionStorage.setItem("last_article_context", JSON.stringify(articleContext));
      } catch (err) {}

      // For story pages, delay the push to get comment count first
      setTimeout(() => {
        const commentNumber = getCommentCount();
        dataLayerObject.comment_number = commentNumber;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(cleanDataLayerPayload ? cleanDataLayerPayload(dataLayerObject) : dataLayerObject);
        console.log('Story page data layer pushed with comment count:', commentNumber);
      }, 1500);

      // Return early to prevent immediate push below
      return;
    } else if (pageType === "section-page" || pageType === "sub-section-page") {
      // Check if section has a parent-id to determine if it's a sub-section
      const hasParent = pageData?.section?.["parent-id"];

      if (hasParent) {
        dataLayerObject.page_type = "sub_section_page";
      } else {
        dataLayerObject.page_type = "section_page";
      }

      // Extract section name with hierarchy from section URL
      const sectionUrl = pageData?.section?.["section-url"] || pageData?.section?.url;
      if (sectionUrl) {
        dataLayerObject.section_name = getSectionHierarchy(sectionUrl);
      }
    } else if (pageType === "tag-page" && pageData) {
      // Tag page specific data
      const tagName = pageData.tags?.name || pageData.tagName || "NA";

      dataLayerObject.page_type = "tag_page";
      dataLayerObject.story_tags = tagName;
    } else if (pageType === "author-page" && pageData?.author) {
      // Author page specific data
      const authorName = pageData.author.name || "NA";
      const authorId = pageData.author.id || "NA";

      dataLayerObject.page_type = "author_page";
      dataLayerObject.author_name = authorName;
      dataLayerObject.author_id = authorId;
    } else if (pageType === "collection-of-collection-page" && pageData?.collection) {
      // Collection page specific data
      dataLayerObject.page_type = "collection_page";
    } else if (pageType === "search-page") {
      // Search page specific data
      dataLayerObject.page_type = "search_page";
    } else if (pageType === "not-found-page" || pageType === "404") {
      // 404 page specific data
      dataLayerObject.page_type = "404_page";
    } else {
      // For other page types, just set the page_type
      dataLayerObject.page_type = pageType || "NA";
    }

    if (
      pageType === "subscribe-page" ||
      dataLayerObject.page_type === "subscribe_plan_page" ||
      dataLayerObject.page_type === "proceed_to_pay_page" ||
      dataLayerObject.page_type === "success_page" ||
      dataLayerObject.page_type === "failure_page"
    ) {
      delete dataLayerObject.premium_article;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(cleanDataLayerPayload ? cleanDataLayerPayload(dataLayerObject) : dataLayerObject);
  }
};

export const getAuthStatus = () => {
  if (typeof window === "undefined") return { status: "anonymous", uuid: "NA" };

  try {
    const userStr = localStorage.getItem("user");
    const accessToken = getAccessToken() || (typeof window !== "undefined" && window.localStorage?.getItem("access_token"));
    const refreshToken = (typeof window !== "undefined" && window.localStorage?.getItem("refresh_token")) || getRefreshToken();

    if (userStr && (accessToken || refreshToken)) {
      const parsedUser = JSON.parse(userStr);
      let uuid = parsedUser?.userData?.id || parsedUser?.id || parsedUser?.user?.id || "NA";

      if (uuid !== "NA") {
        uuid = String(uuid).substring(0, 36);
      }

      return { status: "logged_in", uuid };
    }
  } catch (error) {
    console.error("Error reading auth from localStorage", error);
  }

  return { status: "anonymous", uuid: "NA" };
};

export const pushUserPropertiesUpdateToDataLayer = (userData, isSubscriber, planName = "NA", planPrice = "NA", planId = "NA") => {
  if (typeof window === "undefined") return;

  const dataLayerObject = {
    event: "user_properties_update"
  };

  const authFromStorage = getAuthStatus();

  if (authFromStorage.status === "anonymous" || authFromStorage.status === "not_logged_in") {
    dataLayerObject.auth_status = "anonymous";
    dataLayerObject.subscription_status = "free";
  } else {
    dataLayerObject.auth_status = "logged_in";
    if (authFromStorage.uuid && authFromStorage.uuid !== "NA") {
      dataLayerObject.uuid = authFromStorage.uuid;
    }

    const timeJoined = userData?.userData?.timeJoined || userData?.timeJoined;
    if (timeJoined) {
      dataLayerObject.account_created_date = new Date(timeJoined).toISOString();
    }

    let finalIsSubscriber = isSubscriber;
    let finalPlanName = planName;
    let finalPlanPrice = planPrice;
    let finalPlanId = planId;

    if (authFromStorage.uuid !== "NA") {
      const activePlan = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(`active_plan_name_${authFromStorage.uuid}`) : null;
      const activePrice = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(`active_plan_price_${authFromStorage.uuid}`) : null;
      // Ideally we would also store planId, but it isn't always available in sessionStorage.

      if (activePlan && activePlan !== "NA" && finalIsSubscriber !== "expired") {
        finalIsSubscriber = true;
        if (finalPlanName === "NA") finalPlanName = activePlan;
        if (finalPlanPrice === "NA" && activePrice) finalPlanPrice = activePrice;
      }
    }

    if (finalIsSubscriber === "expired") {
      dataLayerObject.subscription_status = "expired";
      dataLayerObject.plan_name = finalPlanName;
      dataLayerObject.plan_price = finalPlanPrice;
      dataLayerObject.plan_id = finalPlanId;
    } else if (finalIsSubscriber) {
      dataLayerObject.subscription_status = "active";
      dataLayerObject.plan_name = finalPlanName;
      dataLayerObject.plan_price = finalPlanPrice;
      dataLayerObject.plan_id = finalPlanId;
    } else {
      dataLayerObject.subscription_status = "free";
    }
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(dataLayerObject));
};

